import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { EntityListSelectActions } from 'actions';
import NewEntityList from 'containers/components/NewEntityList';
import { getLiteral } from 'utils/getLiteral';

import LeadPreviewRow from './LeadPreviewRow';
import { LEADS_PREVIEW_CHECKBOX_COL_ID, LEADS_PREVIEW_ENTITY } from './constants';
import { LEADS_LITERALS } from './literals';

const LeadList = ({ activeLeads, selectedIds, onSelectionChange }) => {
    const dispatch = useDispatch();
    const activeLeadsRef = useRef(activeLeads);
    activeLeadsRef.current = activeLeads;
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;

    useEffect(() => {
        dispatch(
            EntityListSelectActions.init({
                entity: LEADS_PREVIEW_ENTITY,
                selectionFor: 'leadsProspecting',
                selectionEntity: 'leads',
                isLocal: true,
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        const colId = LEADS_PREVIEW_CHECKBOX_COL_ID;
        const rows = activeLeads.reduce((acc, lead) => {
            acc[lead.id] = selectedIds.some((sid) => String(sid) === String(lead.id));
            return acc;
        }, {});
        const areAllChecked =
            activeLeads.length > 0 &&
            activeLeads.every((lead) => selectedIds.some((sid) => String(sid) === String(lead.id)));
        dispatch(
            EntityListSelectActions.setCheckedWeb5(LEADS_PREVIEW_ENTITY, {
                [colId]: {
                    rows,
                    areAllChecked,
                    lazyAreAllChecked: areAllChecked,
                },
            }),
        );
    }, [activeLeads, dispatch, selectedIds]);

    const components = useMemo(
        () => ({
            LeadPreviewRow,
        }),
        [],
    );

    const onClickCheckbox = useCallback(
        ({ id, isColumn, value, checked, data, rowIndex, total }) => {
            const isChecked = typeof value === 'boolean' ? value : Boolean(checked);

            if (isColumn) {
                const ids = activeLeadsRef.current.map((lead) => lead.id);
                onSelectionChange((current) => {
                    const next = new Set(current);
                    ids.forEach((leadId) => {
                        if (isChecked) next.add(leadId);
                        else next.delete(leadId);
                    });
                    return Array.from(next);
                });
                dispatch(
                    EntityListSelectActions.onEntitySelectAll(
                        LEADS_PREVIEW_ENTITY,
                        isChecked,
                        checked,
                        isChecked ? total : 0,
                    ),
                );
                return;
            }

            onSelectionChange((current) =>
                isChecked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
            );
            dispatch(
                EntityListSelectActions.onEntitySelect(
                    LEADS_PREVIEW_ENTITY,
                    id,
                    isChecked,
                    data,
                    rowIndex,
                    checked,
                ),
            );
        },
        [dispatch, onSelectionChange],
    );

    const toggleRow = useCallback(
        (data, nextChecked) => {
            const colId = LEADS_PREVIEW_CHECKBOX_COL_ID;
            const rows = activeLeadsRef.current.reduce((acc, lead) => {
                const isThisRow = String(lead.id) === String(data.id);
                acc[lead.id] = isThisRow
                    ? nextChecked
                    : selectedIdsRef.current.some((sid) => String(sid) === String(lead.id));
                return acc;
            }, {});
            const areAllChecked =
                activeLeadsRef.current.length > 0 &&
                activeLeadsRef.current.every((lead) => rows[lead.id]);
            const checkedWeb5 = {
                [colId]: {
                    rows,
                    areAllChecked,
                    lazyAreAllChecked: areAllChecked,
                },
            };
            onClickCheckbox({
                id: data.id,
                isColumn: false,
                value: nextChecked,
                checked: checkedWeb5,
                data,
                rowIndex: data.rowIndex,
                total: activeLeadsRef.current.length,
            });
        },
        [onClickCheckbox],
    );

    const listConfig = useMemo(
        () => ({
            entity: LEADS_PREVIEW_ENTITY,
            useFiltersDrawer: true,
            withEntitySelection: true,
            selectionFor: 'leadsProspecting',
            selectionEntity: 'leads',
            isLocalSelection: true,
            columnDefs: [
                {
                    colId: LEADS_PREVIEW_CHECKBOX_COL_ID,
                    headerName: getLiteral(LEADS_LITERALS.leadName),
                    field: 'name',
                    headerComponent: 'headerTextCell',
                    headerComponentParams: {
                        checkbox: {
                            onChange: onClickCheckbox,
                        },
                    },
                    cellRenderer: 'LeadPreviewRow',
                    cellRendererParams: {
                        onToggleLead: toggleRow,
                    },
                    flex: 1,
                    minWidth: 280,
                    resizable: false,
                    sortable: false,
                    lockPosition: true,
                    suppressMovable: true,
                },
            ],
        }),
        [onClickCheckbox, toggleRow],
    );

    const rows = useMemo(
        () =>
            activeLeads.map((lead, rowIndex) => ({
                ...lead,
                Id: lead.id,
                rowIndex,
            })),
        [activeLeads],
    );

    const getCheckedRows = useCallback(
        (data) => ({
            [LEADS_PREVIEW_CHECKBOX_COL_ID]: {
                rows: data.reduce((acc, current) => {
                    acc[current.id] = selectedIds.some((sid) => String(sid) === String(current.id));
                    return acc;
                }, {}),
                areAllChecked:
                    !!data.length &&
                    data.every((lead) =>
                        selectedIds.some((sid) => String(sid) === String(lead.id)),
                    ),
                lazyAreAllChecked:
                    !!data.length &&
                    data.every((lead) =>
                        selectedIds.some((sid) => String(sid) === String(lead.id)),
                    ),
            },
        }),
        [selectedIds],
    );

    return (
        <div className="fm-leads-modal__list">
            <NewEntityList
                id="leads-preview-list"
                entity={LEADS_PREVIEW_ENTITY}
                config={listConfig}
                innerData={{ data: rows, loading: false, error: false }}
                components={components}
                initOnReady={false}
                useLazyLoad={false}
                useCache={false}
                useSort={false}
                useSelectColumns={false}
                useFieldSelector={false}
                getCheckedRows={getCheckedRows}
                rowHeight={72}
                loaderPlaceholderRowHeight={72}
                useFullWidthDivider
            />
        </div>
    );
};

export default memo(LeadList);
